import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import logoLight from "../assets/logo-light.png";

const LINKS = [
  { label: "Home", id: "home", testId: "nav-home" },
  { label: "About Us", id: "about", testId: "nav-about" },
  { label: "Services", id: "services", testId: "nav-services" },
  { label: "Portfolio", id: "portfolio", testId: "nav-portfolio" },
  { label: "Contact", id: "contact", testId: "nav-contact" },
];

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth" });
};

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleClick = (id) => {
    setOpen(false);
    // Delay scroll until the mobile menu's exit animation finishes so the
    // target offset is calculated against the final layout.
    setTimeout(() => scrollTo(id), 300);
  };

  const left = LINKS.slice(0, 2);
  const right = LINKS.slice(2);

  return (
    <motion.header
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className="fixed top-0 left-0 right-0 z-50 header-gradient backdrop-blur-md border-b border-white/10"
    >
      <nav className="max-w-7xl mx-auto px-5 md:px-10">
        {/* Desktop */}
        <div className="hidden md:grid grid-cols-3 items-center h-20">
          <div className="flex items-center gap-8">
            {left.map((l) => (
              <button
                key={l.id}
                data-testid={l.testId}
                onClick={() => handleClick(l.id)}
                className="text-white/85 hover:text-white text-sm tracking-widest uppercase font-medium transition-colors"
              >
                {l.label}
              </button>
            ))}
          </div>
          <button
            data-testid="nav-logo"
            onClick={() => handleClick("home")}
            className="flex justify-center"
          >
            <img src={logoLight} alt="FOXEL Architects" className={`transition-all duration-300 ${scrolled ? "h-12" : "h-16"} w-auto object-contain`} />
          </button>
          <div className="flex items-center justify-end gap-8">
            {right.map((l) => (
              <button
                key={l.id}
                data-testid={l.testId}
                onClick={() => handleClick(l.id)}
                className="text-white/85 hover:text-white text-sm tracking-widest uppercase font-medium transition-colors"
              >
                {l.label}
              </button>
            ))}
          </div>
        </div>

        {/* Mobile */}
        <div className="md:hidden flex items-center justify-between h-16">
          <button data-testid="nav-logo-mobile" onClick={() => handleClick("home")}>
            <img src={logoLight} alt="FOXEL Architects" className="h-14 w-auto object-contain" />
          </button>
          <button
            data-testid="nav-menu-toggle"
            onClick={() => setOpen((o) => !o)}
            className="text-white p-2"
            aria-label="Toggle menu"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden overflow-hidden header-gradient border-t border-white/10"
          >
            <div className="flex flex-col px-6 py-4">
              {LINKS.map((l) => (
                <button
                  key={l.id}
                  data-testid={`${l.testId}-mobile`}
                  onClick={() => handleClick(l.id)}
                  className="text-left py-3 text-white/90 text-sm tracking-widest uppercase font-medium border-b border-white/10 last:border-0"
                >
                  {l.label}
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
